import React, { useState, useRef, useEffect } from 'react';
import { TextInput } from '@etherisc/ui-kit';
// Helper function for conditional classes
function cn(...classes: (string | undefined | false | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface EditableCellProps {
  value: number;
  onChange: (value: number) => void;
  type?: 'number' | 'text';
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  className?: string;
  disabled?: boolean;
  onNavigate?: (direction: 'next' | 'previous') => void;
}

export const EditableCell = React.forwardRef<HTMLDivElement, EditableCellProps>(({
  value,
  onChange,
  type = 'number',
  min,
  max,
  step = 1,
  suffix,
  className,
  disabled = false,
  onNavigate
}, ref) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (disabled) return;
    setEditValue(value.toString());
    setIsEditing(true);
  };

  const handleSave = () => {
    const numValue = parseFloat(editValue);
    if (!isNaN(numValue)) {
      // Apply min/max constraints
      let constrainedValue = numValue;
      if (min !== undefined && constrainedValue < min) constrainedValue = min;
      if (max !== undefined && constrainedValue > max) constrainedValue = max;
      
      console.log('💾 EditableCell: Saving value', { original: value, new: constrainedValue });
      onChange(constrainedValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value.toString());
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      handleSave();
      // Navigate to next/previous cell after saving
      if (onNavigate) {
        onNavigate(e.shiftKey ? 'previous' : 'next');
      }
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <TextInput
          ref={inputRef}
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          min={min}
          max={max}
          step={step}
          className={cn("w-20", className)}
        />
        {suffix && (
          <span className="text-sm text-gray-500">{suffix}</span>
        )}
      </div>
    );
  }

  return (
    <div 
      ref={ref}
      className={cn(
        "flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-2 py-1 -mx-2 -my-1 transition-colors",
        disabled && "cursor-not-allowed opacity-50",
        className
      )}
      onClick={handleStartEdit}
      title={disabled ? "Editing disabled" : "Click to edit"}
    >
      <span className={cn("tabular-nums", disabled && "text-gray-400")}>
        {type === 'number' ? value.toFixed(1) : value}
      </span>
      {suffix && (
        <span className="text-sm text-gray-500">{suffix}</span>
      )}
    </div>
  );
});

EditableCell.displayName = 'EditableCell';
